import {BaseElement, BASIS, OSkinableInit} from "/@back@/commons/basis.js";
import {IDepotUiEnv} from "/@lib@/store/depot.js";
import {IReg, IRegPointer, REG} from "/@lib@/commons/registry.js";
import {IResViewable, OResViewerSingleInit, ResViewerSingle} from "/@back@/store/views/resViewer.js";
import {AccelKeyMgr} from "/@lib@/commons/actions.js";
import {WspApp} from "/@back@/wsp/apps/wspApp.js";
import {IUtBrowserCtx, OUtBrowserInit, UtBrowser} from "/@back@/store/views/utBrowser.js";
import {JSX} from "/@lib@/commons/xml/dom.js";
import {OLastDatasInit} from "/@lib@/commons/lastDatas.js";
import {DOMSH} from "/@lib@/commons/xml/domsh.js";
import {IInfo, IInfoBroker, IInfoBrokerPointer, IInfoConsumer, IInfoProducer, InfoBrokerBasic} from "/@lib@/commons/infos.js";
import {IView, IViewApi, IViewsContainer, OViewVisitOptions, VIEWS} from "/@lib@/commons/views.js";
import {FocusLiveRes} from "/@back@/store/actions/depotActions.js";
import {InfoFocusRes, resPath} from "/@lib@/store/res.js";
import {URLTREE} from "/@lib@/store/urlTree.js";
import {IResChildrenShowModeSvc} from "/@back@/store/areas/resViewAreas.js";

/**
 * Panneau combinant un utBrowser et un ResViewerSingle.
 */
export interface BrowseBoard extends BaseElement, IResViewable {
	initialize(init: OBrowseBoardInit): this;
}

export interface OBrowseBoardInit extends OSkinableInit, OLastDatasInit, IInfoBrokerPointer {
	reg?: IReg<IDepotUiEnv>

	/** Mode simple click mono-selection forcé. */
	selMono?: boolean

	/** Personnalisation du resViewer. */
	resViewer?: OResViewerSingleInit

	/** Personnalisation de l'utBrowser. */
	utBrowser?: OUtBrowserInit
}

export class BrowseBoard extends BaseElement implements IResViewable, IView, IViewsContainer {
	reg: IReg<IDepotUiEnv>;

	infoBroker: BrowseBoardInfoBroker;

	accelKeyMgr: AccelKeyMgr<IRegPointer<IDepotUiEnv>>;

	protected _initialize(init: OBrowseBoardInit) {
		this.reg = REG.createSubReg(this.findReg(init));
		this.infoBroker = this.reg.env.infoBroker = new BrowseBoardInfoBroker(this, init.infoBroker || this.reg.parentReg.env.infoBroker);

		const widthDetector = matchMedia("(max-width: 640px)")
		this.reg.registerSvc<IResChildrenShowModeSvc>("storeResChildrenShowMode", 1, (): 'children' | 'tree' => {
			return widthDetector.matches ? 'tree' : 'children';
		});

		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this.reg.installSkin('webzone:panel', sr);
		this._initAndInstallSkin(this.localName, init);

		this.accelKeyMgr = new AccelKeyMgr();
		this.addEventListener('keydown', function (this: BrowseBoard, ev: KeyboardEvent) {
			this.accelKeyMgr.handleKeyboardEvent(ev, this)
		});
		this.accelKeyMgr.initFromMapActions(this.reg.getListAsMap("accelkeys:BrowseBoard:global"));

		const resViewerInit = BASIS.newInit(init.resViewer, this.reg);
		resViewerInit.lastDatas = init.lastDatas;
		const resViewer = new ResViewerSingle().initialize(resViewerInit);
		this.reg.installSkin('webzone:page', resViewer.shadowRoot);

		const utBrowserInit = BASIS.newInit(init.utBrowser, this.reg);
		utBrowserInit.infoBroker = utBrowserInit.resHandlingReact = this.infoBroker;
		utBrowserInit.defaultAction = new FocusLiveRes();
		if (init.selMono) utBrowserInit.resHandlingMode = "sel";
		if (utBrowserInit.nodeFilter === undefined) utBrowserInit.nodeFilter = (n) => !URLTREE.isQueryPath(n.n);

		if (utBrowserInit.actions === undefined) {
			this.fillDefaultActions();
			utBrowserInit.actions = this.reg.mergeLists("actions:BrowseBoard:utBrowser", "actions:store:resList");
			if (!utBrowserInit.accelKeyMgr) utBrowserInit.accelKeyMgr = new AccelKeyMgr<IUtBrowserCtx>().initFromMapActions(this.reg.mergeListsAsMap("accelkeys:BrowseBoard:utBrowser", "accelkeys:store:resList"));
		}

		const utBrowser = new UtBrowser().initialize(utBrowserInit);

		const resizer = <c-resizer c-orient="row">
			<c-collapser c-orient="row" panel-pos="prev"/>
		</c-resizer>;

		function switchWidth(ev: MediaQueryList | MediaQueryListEvent) {
			if (ev.matches) {
				utBrowser.style.display = "none";
				resizer.style.display = "none";
			} else {
				utBrowser.style.display = null;
				resizer.style.display = null;
			}
		}

		switchWidth(widthDetector);
		widthDetector.addEventListener('change', switchWidth);

		utBrowser.setAttribute("c-resizable", "");
		sr.append(utBrowser, resizer, <div id="main" c-resizable="">{resViewer}</div>);
	}

	protected fillDefaultActions() {
		// const reg = this.reg;
		// function add(action: IAction<IDepotCtx>, accelKey?: string) {
		// 	reg.addToList("actions:BrowseBoard:utBrowser", action.getId(), 1, action);
		// 	if (accelKey) reg.addToList("accelkeys:BrowseBoard:utBrowser", action.getId(), 1, action);
		// }
		// add(new Xxx());
	}

	setResPath(resPath: resPath) {
		this.infoBroker.dispatchInfo(new InfoFocusRes(resPath), this);
	}

	shown: boolean;

	onViewShown() {
		this.shown = true;
		VIEWS.onContainerShown(this);
	}

	onViewHidden(closed?: boolean) {
		this.shown = false;
		if (closed) this.infoBroker.close();
		VIEWS.onContainerHidden(this, closed);
	}

	visitViews(visitor: (view: IViewApi) => any, options?: OViewVisitOptions): any {
		if (options?.visible && !this.shown) return;
		return VIEWS.visitDescendants(this.shadowRoot, visitor);
	}

	visitViewsAsync(visitor: (view: IViewApi) => Promise<any>, options?: OViewVisitOptions): Promise<any> {
		if (options?.visible && !this.shown) return;
		return VIEWS.visitDescendantsAsync(this.shadowRoot, visitor);
	}
}


REG.reg.registerSkin('store-browse-board', 1, /* language=CSS */ `
	:host {
	  flex: 1;
	  display: flex;
	  min-height: 0;
	  min-width: 0;
	  flex-direction: row;
	  padding: 5px;
  }

  [hidden] {
	  display: none;
  }

  store-ut-browser {
	  flex: 1 1 5em;
	  border: 1px solid var(--border-color);
	  border-radius: 7px;
  }

  c-resizer[c-orient=row] {
	  width: 10px;
	  background-color: transparent;
  }

  c-collapser {
	  background-color: var(--panel-bgcolor);
  }

  #main {
	  flex: 3 1 20em;
	  display: flex;
	  border: 1px solid var(--border-color);
	  border-radius: 7px;
	  overflow: hidden;
  }

  store-resviewer-single {
	  flex: 1;
  }
`);

customElements.define('store-browse-board', BrowseBoard);

class BrowseBoardInfoBroker extends InfoBrokerBasic implements IInfoConsumer {
	constructor(public view: BrowseBoard, public parent: IInfoBroker) {
		super();
		if (parent) parent.addConsumer(this);
	}

	dispatchInfo(info: IInfo, from: IInfoProducer) {
		super.dispatchInfo(info, from);
		if (this.parent && info.infoHolders.indexOf(this) < 0) {
			this.parent.dispatchInfo(info, this);
		}
	}

	close() {
		this.parent?.removeConsumer(this);
	}

	//Info from parent
	onInfo(info: IInfo) {
		if (this.view.shown) this.dispatchInfo(info, this);
	}
}